import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

import { BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateFormatterService } from '@frontend/sports/common/core/feature/date-formatter-service';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { Store, createSelector } from '@ngrx/store';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';
import { Observable, map, of } from 'rxjs';

import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { GroupEnabledPickIds, PickId } from '../../../core/picks/pick-id';
import { getOptionNameFromBetslipPick } from '../../../core/utils';
import { removePicksFromGroup } from '../../picks/grouped-picks.actions';
import { BetslipMarketSubTypeLogoService } from '../../picks/services/betslip-market-sub-type-logo.service';
import { selectCurrentBetslipTypeErrors, selectCurrentPicksErrors } from '../../validation/selectors';
import { groupPickHasWarning } from '../../validation/services/utils/betslip-errors-utils';
import { BaseBetBuilderComponent } from './base-betbuilder.component';
import { BetBuilderSelectionModel } from './betbuilder-selection.model';

const selectGroupPickHasWarning = (pickId: PickId) =>
    createSelector(selectCurrentBetslipTypeErrors, (typeErrors) =>
        typeErrors
            .filter(groupPickHasWarning)
            .flatMap((groupPickError) => groupPickError.invalidPickIds)
            .some((invalidPickId) => invalidPickId.isEqual(pickId)),
    );

@Component({
    selector: 'bs-betslip-group-pick',
    templateUrl: './betslip-group-pick.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetslipGroupPickComponent extends BaseBetBuilderComponent implements OnChanges, OnInit {
    @Input() pick: BaseBetslipGroupPick;

    @HostBinding('class') className = 'betBuilder-pick';
    date: string;
    selections: Observable<BetBuilderSelectionModel[]>;
    hasWarning$: Observable<boolean>;

    constructor(
        interpolationPipe: StringInterpolationPipe,
        private store: Store,
        sitecore: Sitecore,
        epcotConfigService: EpcotConfigService,
        betslipConfig: BetslipConfig,
        changeDetectorRef: ChangeDetectorRef,
        private dateFormatterService: DateFormatterService,
        private betslipMarketSubTypeLogoService: BetslipMarketSubTypeLogoService,
    ) {
        super(interpolationPipe, sitecore, epcotConfigService, changeDetectorRef, betslipConfig);
        this.setIsCombo(true);
    }

    ngOnInit(): void {
        this.selections = this.store.select(selectCurrentPicksErrors).pipe(
            map((pickErrors) => {
                return this.pick.getLegs().map((p) => {
                    return {
                        longId: p.id.toString(),
                        name: getOptionNameFromBetslipPick(p),
                        hasWarning:
                            this.pick.legIsNoncombinable(p.id) ||
                            (pickErrors[p.id.toString()]?.some((t) => t.type === PlacementErrorType.ComboPrevention) ?? false),
                        oddsState: this.pick.getOddsStateForLegId(p.id),
                        marketSubTypeSignpostingText: this.betslipMarketSubTypeLogoService.getMarketSubTypeSignpostingText(p),
                    };
                });
            }),
        );
    }

    removeBetBuilderSelection(selection: BetBuilderSelectionModel): void {
        this.store.dispatch(
            //why calling external actions within betslip ?
            removePicksFromGroup({
                pickIds: [this.pick.getLegs().find((t) => t.id.toString() === selection.longId)?.id! as GroupEnabledPickIds],
                groupInfo: this.pick.groupInfo,
            }),
        );
    }

    ngOnChanges(): void {
        this.date = this.dateFormatterService.formatDateWithTime(this.pick.eventDate);

        this.hasWarning$ = this.pick.groupHasUncombinableLeg() ? of(false) : this.store.select(selectGroupPickHasWarning(this.pick.id));
    }

    override get selectionsLength() {
        return this.pick.getLegs().length;
    }
}
